import { useState, ChangeEvent, MouseEvent, useEffect } from 'react';
import ButtonLoader from '../buttonLoader/ButtonLoader';
import ReactToastify from '../../hooks/useReactToastify';
import { useLazyGetEmployeeLeaveBalanceQuery } from '../../features/apis/requestApi';
import { useChangeLeaveMutation } from '../../features/apis/authApi';
import { FormControl, FormLabel, Input, SimpleGrid } from '@chakra-ui/react';

type LeaveEditProps = {
  current_user?: {
    _id?: string;
    role?: string;
    assigned_HR?: any;
  };
  profileUser: {
    _id?: string;
    name: string | undefined;
    email: string | undefined;
    leave_data: any[];
  };
  onClose: () => void;
};

const LeaveEdit = ({ current_user, profileUser, onClose }: LeaveEditProps) => {
  const { name } = profileUser;
  const [getLeaveBalance, { data }] = useLazyGetEmployeeLeaveBalanceQuery();
  const [leavesdata, setLeavesdata] = useState<Record<string, number | ''>>({
    general: '',
    bereavement: '',
    optional: '',
    anniversary: '',
    sick: '',
    marriage: '',
    birthday: '',
    maternity: '',
    paternity: '',
  });

  const [loadingState, setLoadingState] = useState(false);
  const [changeLeave] = useChangeLeaveMutation();

  const inputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setLeavesdata((prev) => ({
      ...prev,
      [name]: value === '' ? '' : Number(value),
    }));
  };

  interface Leave {
    leave_type: string;
    leave_left: number;
  }

  useEffect(() => {
    if (profileUser._id) getLeaveBalance(profileUser._id);
  }, [getLeaveBalance, profileUser._id]);

  useEffect(() => {
    if (data?.leaveBalance) {
      const leaveObj: Record<string, number> = {};
      data.leaveBalance.forEach((leave: Leave) => {
        const raw = (leave as any).leave_left;
        const numeric =
          typeof raw === 'number'
            ? raw
            : typeof raw === 'string'
            ? Number(raw)
            : raw?.$numberDecimal !== undefined
            ? Number(raw.$numberDecimal)
            : 0;
        leaveObj[leave.leave_type] = numeric || 0;
      });
      setLeavesdata((prev) => ({ ...prev, ...leaveObj }));
    }
  }, [data?.leaveBalance]);
  const hasPositiveLeave = Object.values(leavesdata).some(
    (val) => typeof val === 'number' && val > 0
  );

  const changeLeaveHandler = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const hasAtLeastOneValue = Object.values(leavesdata).some(
      (val) => typeof val === 'number' && val > 0
    );

    if (!hasAtLeastOneValue) {
      ReactToastify({
        message: 'Please enter at least one leave value',
        success: false,
      });
      return;
    }

    setLoadingState(true);
    const leavesArray = Object.entries(leavesdata).map(
      ([leave_type, leave_left]) => ({
        leave_type,
        leave_left: leave_left === '' ? 0 : leave_left,
      })
    );

    try {
      const response = await changeLeave({
        role: current_user?.role,
        leave_data: leavesArray,
        email: profileUser.email,
        name: profileUser.name,
      }).unwrap();

      ReactToastify({
        message: response.log,
        success: true,
      });
      setLoadingState(false);
      onClose();
    } catch (err: any) {
      setLoadingState(false);
      ReactToastify({
        message: err.data?.log || 'Error updating leaves',
        success: false,
      });
    }
  };
  return (
    <div className="bg-white rounded-md w-full max-w-5xl p-6 max-h-[80vh] overflow-y-auto relative">
      <h1 className="text-sm font-semibold text-left">
        {name?.split(' ')[0]}&apos;s Leaves
      </h1>
      <div className="border-b-[0.5px] border-[#D4D4D4] mt-2 mb-1" />
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={6}>
        {Object.entries(leavesdata).map(([key, value]) => (
          <FormControl key={key}>
            <FormLabel htmlFor={key} fontSize="12px" fontWeight="bold">
              {key.charAt(0).toUpperCase() + key.slice(1)} leaves
            </FormLabel>
            <Input
              id={key}
              name={key}
              type="number"
              placeholder={`Enter ${key} leaves`}
              value={value}
              onChange={inputHandler}
              fontSize="11px"
              border="0.5px solid"
              borderColor="#D4D4D4"
              _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
              _hover={{ borderColor: '#D4D4D4' }}
            />
          </FormControl>
        ))}
      </SimpleGrid>

      <div className="flex justify-end mt-6">
        <button
          onClick={changeLeaveHandler}
          disabled={loadingState || !hasPositiveLeave}
          className={`bg-[linear-gradient(to_right,#6535EB,#9F3198)] text-white text-xs font-medium rounded-md border-none px-4 py-3 ${
            loadingState || !hasPositiveLeave
              ? 'opacity-60 cursor-not-allowed'
              : 'opacity-100 cursor-pointer'
          }`}
        >
          {loadingState ? (
            <>
              <ButtonLoader />
              <span>Submitting...</span>
            </>
          ) : (
            <span>Update Leaves</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default LeaveEdit;
